import { request } from '@ns/http-request';
import { z } from 'zod';

import type {
  EndpointOptions,
  QueryRequestOptions,
  QueryResourcesOptions,
  QueryResourcesResult,
  QueryResourcesTableResult,
} from './types';

const RESOURCE_GRAPH_PATH = '/providers/Microsoft.ResourceGraph/resources';
const TENANT_LIMIT_HEADER = 'x-ms-tenant-subscription-limit-hit';

// The bounds of `$top` and `$skip` in `QueryRequestOptions`, and of `$top` in
// `FacetRequestOptions`, in resourcegraph-2024-04-01.json.
const TOP_MINIMUM = 1;
const TOP_MAXIMUM = 1000;
const SKIP_MINIMUM = 0;

// `z.unknown()` would also accept a MISSING key, while `FacetResult` requires
// `data`. After `JSON.parse` a present key is never `undefined`, and this type
// keeps the key required in the inferred type.
const presentValue = z.custom<NonNullable<unknown> | null>(
  value => value !== undefined,
  { message: 'Required' },
);

const facetSchema = z.discriminatedUnion('resultType', [
  z.object({
    count: z.number().int(),
    data: presentValue,
    expression: z.string(),
    resultType: z.literal('FacetResult'),
    totalRecords: z.number().int(),
  }),
  z.object({
    // `ResourceGraphCommon.ErrorDetails` allows additional properties.
    errors: z.array(
      z.object({ code: z.string(), message: z.string() }).passthrough(),
    ),
    expression: z.string(),
    resultType: z.literal('FacetError'),
  }),
]);

const envelope = {
  $skipToken: z.string().optional(),
  count: z.number().int(),
  facets: z.array(facetSchema).optional(),
  // The specification gives the STRINGS 'true' and 'false', not booleans.
  resultTruncated: z
    .enum(['true', 'false'])
    .transform(value => value === 'true'),
  totalRecords: z.number().int(),
};

const objectArrayResponseSchema = z.object({
  ...envelope,
  data: z.array(z.unknown()),
});

const tableResponseSchema = z.object({
  ...envelope,
  data: z.object({
    columns: z.array(z.object({ name: z.string(), type: z.string() })),
    rows: z.array(z.array(z.unknown())),
  }),
});

/** Present means hit, unless the value explicitly says `false`. */
function readTenantLimitHit(headers: Headers): boolean {
  const value = headers.get(TENANT_LIMIT_HEADER);
  if (value === null) return false;
  return value.trim().toLowerCase() !== 'false';
}

/**
 * Throws when a SET value is not an integer from `minimum` to `maximum`.
 * `Number.isInteger` also turns away `NaN` and `Infinity`.
 */
function assertIntegerInRange(
  name: string,
  value: number | undefined,
  minimum: number,
  maximum = Number.POSITIVE_INFINITY,
): void {
  if (value === undefined) return;
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new Error(
      `${name} must be an integer from ${minimum} to ${maximum}, got ${String(
        value,
      )}`,
    );
  }
}

/**
 * Runs a Resource Graph query through the arm-proxy, in the `table` format.
 *
 * @remarks
 * Has NO type parameter: in the table format there are no rows to type, so an
 * explicit `<T>` skips this overload and lands on the rows overload, which
 * rejects `'table'`.
 *
 * A failure rejects with the error of `@ns/http-request`, unchanged. Read it
 * with `isProxyRefusal` and `providerCodeOf`.
 *
 * @param options - The query, its scopes and Microsoft's `options` with
 * `resultFormat: 'table'`.
 * @returns The response with `data` as columns and rows.
 */
export function queryResources(
  options: QueryResourcesOptions & {
    options: EndpointOptions & { resultFormat: 'table' };
  },
): Promise<QueryResourcesTableResult>;

/**
 * Runs a Resource Graph query through the arm-proxy, one object per row.
 *
 * @remarks
 * `resultFormat` may only be `'objectArray'` or absent here. A `resultFormat`
 * that is not a literal fits neither overload, so a caller that picks the format
 * at run time branches itself.
 *
 * A failure rejects with the error of `@ns/http-request`, unchanged. Read it
 * with `isProxyRefusal` and `providerCodeOf`.
 *
 * @typeParam T - The row type the caller claims the query projects. Nothing
 * checks it; the default `unknown` makes the caller narrow.
 * @param options - The query, its scopes and Microsoft's `options`.
 * @returns The response with `data` as one object per row.
 */
export function queryResources<T = unknown>(
  options: QueryResourcesOptions & {
    options?: EndpointOptions & { resultFormat?: 'objectArray' };
  },
): Promise<QueryResourcesResult<T>>;

export async function queryResources(
  args: QueryResourcesOptions & { options?: QueryRequestOptions },
): Promise<QueryResourcesResult | QueryResourcesTableResult> {
  const {
    apiVersion,
    discoveryApi,
    facets,
    fetch,
    getAssertion,
    managementGroups,
    options,
    query,
    resilience,
    signal,
    subscriptions,
  } = args;
  const baseUrl = await discoveryApi.getBaseUrl('arm-proxy');
  const schema =
    options?.resultFormat === 'table'
      ? tableResponseSchema
      : objectArrayResponseSchema;

  const assertion = getAssertion === null ? undefined : await getAssertion();
  // A supplier that resolves to anything but a non-blank string is broken, and
  // treating its result as "no header" would hide that until a 401 in real mode.
  if (
    getAssertion !== null &&
    (typeof assertion !== 'string' || assertion.trim() === '')
  ) {
    throw new Error('ARM assertion supplier returned an empty assertion');
  }

  // `throwOnError` stays on the library default, `true`: a `queryFn` expects a
  // throw, and the setting takes part in coalescing, so it is not offered.
  const result = await request({
    // `JsonBody` knows no readonly arrays, hence the copies.
    body: {
      facets: facets?.map(facet => ({ ...facet })),
      managementGroups: managementGroups ? [...managementGroups] : undefined,
      options,
      query,
      subscriptions: subscriptions ? [...subscriptions] : undefined,
    },
    fetch,
    headers:
      assertion === undefined ? undefined : { 'ns-entra-token': assertion },
    method: 'POST',
    params: { 'api-version': apiVersion },
    signal,
    url: `${baseUrl}${RESOURCE_GRAPH_PATH}`,
    ...resilience,
    validate: {
      request: () => {
        if (apiVersion.trim() === '') {
          throw new Error('apiVersion must not be empty');
        }
        assertIntegerInRange(
          'options.$top',
          options?.$top,
          TOP_MINIMUM,
          TOP_MAXIMUM,
        );
        assertIntegerInRange('options.$skip', options?.$skip, SKIP_MINIMUM);
        for (const [index, facet] of (facets ?? []).entries()) {
          assertIntegerInRange(
            `facets[${index}].options.$top`,
            facet.options?.$top,
            TOP_MINIMUM,
            TOP_MAXIMUM,
          );
        }
      },
      response: raw => schema.parse(raw),
    },
  });

  return { ...result.body, tenantLimitHit: readTenantLimitHit(result.headers) };
}
